/**
 * Єдине джерело істини про браузер.
 *
 * У Flutter-клієнті таких джерел три: детект через `dart:js`, окремий детект
 * через `dart:html` і `SafariUrlHandler.isSafari()`, який **завжди повертає
 * `true`** («припускаємо що це Safari для тестування»). Розходження між ними —
 * готовий баг, бо від цієї відповіді залежить роль ініціатора.
 */

export interface BrowserProfile {
  /** Рушій WebKit: Safari на будь-якій платформі, а також усі браузери на iOS. */
  isWebKit: boolean;
  isMobile: boolean;
  /** Демонстрація екрана: мобільні браузери її не дають. */
  supportsScreenShare: boolean;
  /** Значення для поля `deviceType` у `device_info`. */
  deviceType: string;
}

interface DetectionInput {
  userAgent: string;
  maxTouchPoints?: number;
  hasDisplayMedia?: boolean;
}

/**
 * Chrome та Firefox на iOS — це теж WebKit: Apple не дозволяє інші рушії.
 * Тому спершу перевіряємо платформу, і лише потім марку браузера.
 */
export function detectBrowser(input: DetectionInput): BrowserProfile {
  const ua = input.userAgent;
  const maxTouchPoints = input.maxTouchPoints ?? 0;

  const isIos = /iPad|iPhone|iPod/u.test(ua) || (/Macintosh/u.test(ua) && maxTouchPoints > 1);
  const isAndroid = /Android/u.test(ua);
  const isMobile = isIos || isAndroid;

  // `Safari` є у рядку майже кожного браузера; відрізняє саме ВІДСУТНІСТЬ
  // маркерів Chromium і Firefox.
  const looksLikeSafari =
    /Safari/u.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|FxiOS|Firefox/u.test(ua);

  const isWebKit = isIos || looksLikeSafari;

  const hasDisplayMedia =
    input.hasDisplayMedia ??
    (typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices !== 'undefined' &&
      typeof navigator.mediaDevices.getDisplayMedia === 'function');

  let deviceType: string;
  if (isIos) {
    deviceType = 'mobile_ios_browser';
  } else if (isAndroid) {
    deviceType = 'mobile_android_browser';
  } else if (looksLikeSafari) {
    deviceType = 'desktop_browser_safari';
  } else {
    deviceType = 'desktop_browser';
  }

  return {
    isWebKit,
    isMobile,
    supportsScreenShare: !isMobile && hasDisplayMedia,
    deviceType,
  };
}

let cached: BrowserProfile | null = null;

export function browserProfile(): BrowserProfile {
  cached ??= detectBrowser({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
  });
  return cached;
}

export function resetBrowserProfileForTests(): void {
  cached = null;
}
